import React, { useMemo, useState, useEffect } from 'react'
import { useTheme } from '../../../../Utils/useTheme'
import { Column, FilterOp, FilterSpec } from '../types'
import { PopoverPortal, useAnchorPosition } from './PopoverPortal'

type OpItem = { op: FilterOp; label: string }

const OPS_TEXT: OpItem[] = [
  { op: 'contains', label: 'Contains' },
  { op: 'starts_with', label: 'Starts with' },
  { op: 'ends_with', label: 'Ends with' },
  { op: 'eq', label: 'Equals' },
  { op: 'neq', label: 'Not equals' },
  { op: 'is_empty', label: 'Is empty' },
  { op: 'not_empty', label: 'Not empty' }
]

const OPS_NUM: OpItem[] = [
  { op: 'eq', label: '=' },
  { op: 'neq', label: '≠' },
  { op: 'gt', label: '>' },
  { op: 'gte', label: '≥' },
  { op: 'lt', label: '<' },
  { op: 'lte', label: '≤' },
  { op: 'between', label: 'Between' },
  { op: 'is_empty', label: 'Is empty' },
  { op: 'not_empty', label: 'Not empty' }
]

const OPS_BOOL: OpItem[] = [
  { op: 'eq', label: 'True' },
  { op: 'neq', label: 'False' }
]

const OPS_WITHOUT_VALUE: FilterOp[] = ['is_empty', 'not_empty']

export const FilterPopover = ({
  anchorRect,
  column,
  value,
  onChange,
  onClose
}: {
  anchorRect: DOMRect | null
  column: Column<any>
  value?: FilterSpec
  onChange: (next: FilterSpec | null) => void
  onClose: () => void
}) => {
  const { border, text, hover } = useTheme()

  const ops = useMemo<OpItem[]>(() => {
    if (column.type === 'number' || column.type === 'currency') return OPS_NUM
    if (column.type === 'boolean') return OPS_BOOL
    return OPS_TEXT
  }, [column.type])

  const defaultOp = useMemo<FilterOp>(() => {
    if (value?.op && ops.some(o => o.op === value.op)) return value.op
    return ops[0].op
  }, [value?.op, ops])

  const [op, setOp] = useState<FilterOp>(defaultOp)
  const [v1, setV1] = useState<string>(
    value?.value !== undefined ? String(value.value) : ''
  )
  const [v2, setV2] = useState<string>(
    value?.value2 !== undefined ? String(value.value2) : ''
  )

  useEffect(() => {
    setOp(defaultOp)
  }, [defaultOp])

  const needsValue = !OPS_WITHOUT_VALUE.includes(op)
  const needsValue2 = op === 'between'

  useEffect(() => {
    if (!needsValue) {
      setV1('')
      setV2('')
    }
    if (!needsValue2) {
      setV2('')
    }
  }, [op])

  const pos = useAnchorPosition(
    anchorRect,
    320,
    needsValue2 ? 286 : 248,
    'right'
  )

  return (
    <PopoverPortal>
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          width: 320,
          background: 'white',
          border: `1px solid ${border}`,
          borderRadius: 14,
          padding: 12,
          boxShadow: 'rgba(0,0,0,0.22) 0px 18px 46px',
          zIndex: 999999
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: text,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {column.title}
          </div>

          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 10,
              border: `1px solid ${border}`,
              background: 'rgba(17,24,39,0.06)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={e => (e.currentTarget.style.background = hover)}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'rgba(17,24,39,0.06)')
            }
            onClick={onClose}
          >
            ✕
          </div>
        </div>

        <div style={{ height: 10 }} />

        {/* BODY */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <select
            value={op}
            onChange={e => setOp(e.target.value as FilterOp)}
            style={{
              height: 36,
              borderRadius: 12,
              border: `1px solid ${border}`,
              padding: '0 10px',
              fontSize: 13
            }}
          >
            {ops.map(x => (
              <option key={x.op} value={x.op}>
                {x.label}
              </option>
            ))}
          </select>

          {needsValue && column.type !== 'boolean' && (
            <input
              value={v1}
              onChange={e => setV1(e.target.value)}
              placeholder="Value"
              style={{
                height: 36,
                borderRadius: 12,
                border: `1px solid ${border}`,
                padding: '0 10px',
                fontSize: 13
              }}
            />
          )}

          {column.type === 'boolean' && needsValue && (
            <select
              value={v1}
              onChange={e => setV1(e.target.value)}
              style={{
                height: 36,
                borderRadius: 12,
                border: `1px solid ${border}`,
                padding: '0 10px',
                fontSize: 13
              }}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          )}

          {needsValue2 && (
            <input
              value={v2}
              onChange={e => setV2(e.target.value)}
              placeholder="Value 2"
              style={{
                height: 36,
                borderRadius: 12,
                border: `1px solid ${border}`,
                padding: '0 10px',
                fontSize: 13
              }}
            />
          )}

          {/* ACTIONS */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div
              style={{
                flex: 1,
                height: 36,
                borderRadius: 12,
                border: `1px solid ${border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontWeight: 600
              }}
              onMouseEnter={e => (e.currentTarget.style.background = hover)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              onClick={() => onChange(null)}
            >
              Clear
            </div>

            <div
              style={{
                flex: 1,
                height: 36,
                borderRadius: 12,
                border: `1px solid ${border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontWeight: 600
              }}
              onMouseEnter={e => (e.currentTarget.style.background = hover)}
              onMouseLeave={e => (e.currentTarget.style.background = 'white')}
              onClick={() => {
                const next: FilterSpec = { key: column.key, op }
                if (needsValue) next.value = column.type === 'boolean' ? v1 === 'true' : v1
                if (needsValue2) next.value2 = v2
                onChange(next)
              }}
            >
              Apply
            </div>
          </div>
        </div>
      </div>
    </PopoverPortal>
  )
}
